import { Check } from 'lucide-react';
import { DropdownMenu as Primitive } from 'radix-ui';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils';

/** DropdownMenu de shadcn/ui con la piel de vidrio de Consalud. */
export const DropdownMenu = Primitive.Root;
export const DropdownMenuTrigger = Primitive.Trigger;
export const DropdownMenuGroup = Primitive.Group;

export function DropdownMenuContent({
  className,
  sideOffset = 8,
  ...props
}: ComponentProps<typeof Primitive.Content>) {
  return (
    <Primitive.Portal>
      <Primitive.Content
        sideOffset={sideOffset}
        className={cn(
          'vidrio-fuerte z-50 min-w-60 overflow-hidden rounded-[14px] p-1.5 text-texto',
          'data-[state=open]:animate-[fadeIn_160ms_ease-out]',
          className,
        )}
        {...props}
      />
    </Primitive.Portal>
  );
}

export function DropdownMenuItem({ className, ...props }: ComponentProps<typeof Primitive.Item>) {
  return (
    <Primitive.Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-lg px-3 py-2 text-sm font-semibold text-texto-2 outline-none select-none',
        'data-[highlighted]:bg-[var(--vidrio-fuerte)] data-[highlighted]:text-texto data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
        className,
      )}
      {...props}
    />
  );
}

/** Opción con casilla; `onSelect` con `preventDefault()` deja el menú abierto (multiselección). */
export function DropdownMenuCheckboxItem({
  className,
  children,
  ...props
}: ComponentProps<typeof Primitive.CheckboxItem>) {
  return (
    <Primitive.CheckboxItem
      className={cn(
        'group flex cursor-pointer items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-semibold text-texto-2 outline-none select-none',
        'data-[highlighted]:bg-[var(--vidrio-fuerte)] data-[highlighted]:text-texto data-[state=checked]:text-texto data-[disabled]:pointer-events-none data-[disabled]:opacity-45',
        className,
      )}
      {...props}
    >
      <span
        aria-hidden
        className="grid size-4 shrink-0 place-items-center rounded border border-[var(--borde-acento)] group-data-[state=checked]:border-azul-600 group-data-[state=checked]:bg-azul-600"
      >
        <Primitive.ItemIndicator>
          <Check className="size-3 text-white" strokeWidth={3.5} />
        </Primitive.ItemIndicator>
      </span>
      {children}
    </Primitive.CheckboxItem>
  );
}

export function DropdownMenuLabel({ className, ...props }: ComponentProps<typeof Primitive.Label>) {
  return (
    <Primitive.Label
      className={cn(
        'px-3 pt-2 pb-1 text-xs font-bold tracking-wide text-texto-3 uppercase',
        className,
      )}
      {...props}
    />
  );
}

export function DropdownMenuSeparator({
  className,
  ...props
}: ComponentProps<typeof Primitive.Separator>) {
  return (
    <Primitive.Separator
      className={cn('my-1.5 h-px bg-[var(--separador)]', className)}
      {...props}
    />
  );
}
